import { pageFaqAdditions } from "@/data/pageFaqs";
import { useTrackView } from "@/lib/useTrackView";
import { createFileRoute, Link, Navigate } from "@tanstack/react-router";
import { DECISION_SPRINT_LIVE } from "@/lib/flags";
import { m, useReducedMotion } from "framer-motion";
import { ArrowRight, Calendar, ChevronRight, Compass } from "lucide-react";
import { BookFitCall } from "@/components/site/BookFitCall";
import { PhotoBand } from "@/components/site/PhotoBand";
import { RelatedServices } from "@/components/site/RelatedServices";
import { RelatedExpertise } from "@/components/site/CrossLinks";
import { DiagramFrame, rise } from "@/components/diagrams/DiagramFrame";
import { findRow, formatPrice } from "@/data/pricing";
import { SCOPE_EXCLUSION } from "@/data/terms";
import {
  breadcrumbSchema,
  canonicalLinks,
  faqPageSchema,
  jsonLd,
  ogImage,
  ogUrl,
  serviceSchema,
  webPageSchema, absoluteUrl} from "@/lib/seo";
import decisionPhoto from "@/assets/photos/whiteboard-planning.webp";

const priceOf = (name: string) => formatPrice(findRow(name));

const TITLE = "Decision Sprint: one big decision, answered | Ajility";
const DESCRIPTION =
  "One high-stakes operational or commercial question turned into a clear recommendation, scored options and an agreed next move, in three days to three weeks.";

const questions = [
  "Should I hire a GM or COO, or promote from inside?",
  "Should I change my pricing, and by how much?",
  "Buy, build or replace the system?",
  "Open the second site, wait, or partner?",
  "Take the big contract or walk away?",
  "Outsource the function or keep it?",
];

const steps = [
  {
    title: "Decision brief and kick-off",
    desc: "Define the decision, constraints, success criteria and deadline.",
  },
  {
    title: "Evidence",
    desc: "The numbers you supply plus stakeholder conversations.",
  },
  {
    title: "Options",
    desc: "Three to five viable paths, always including do nothing or defer, scored against weighted criteria.",
  },
  {
    title: "Decision memo",
    desc: "The recommendation, the scorecard, the assumptions, what would change the answer, and the first 30, 60 and 90 days.",
  },
  {
    title: "Facilitated decision session",
    desc: "You and the people who have to execute, in one room, leaving with the call made and owners named.",
  },
];

const tiers = [
  {
    name: "Rapid Decision Review",
    price: priceOf("Rapid Decision Review"),
    duration: "3 business days",
    standard: false,
    forWho:
      "One bounded decision, documents supplied, up to two stakeholder conversations, no modelling.",
    youGet: [
      "60-minute framing call",
      "Two to four options scored",
      "Four to six page memo",
      "60-minute decision meeting",
    ],
  },
  {
    name: "Decision Sprint",
    price: priceOf("Decision Sprint"),
    duration: "9 business days",
    standard: true,
    forWho:
      "One consequential decision, up to four stakeholder interviews, a financial and operational read.",
    youGet: [
      "Three to five options scored",
      "Six to nine page memo",
      "30, 60 and 90 day first steps",
      "90-minute facilitated decision session",
    ],
  },
  {
    name: "Deep Decision Case",
    price: priceOf("Deep Decision Case"),
    duration: "3 weeks",
    standard: false,
    forWho:
      "Material decisions: second site, restructure, core system, customer concentration.",
    youGet: [
      "Six to eight interviews",
      "Scenarios and sensitivity",
      "Nine to twelve page memo",
      "Two-hour decision session",
      "30-day launch plan",
    ],
  },
];

const criteria = [
  { name: "Economic return", weight: 25 },
  { name: "Cash and payback", weight: 20 },
  { name: "Execution feasibility", weight: 20 },
  { name: "Operational capacity", weight: 15 },
  { name: "Downside risk", weight: 10 },
  { name: "Strategic fit and reversibility", weight: 10 },
];

const faqs = [
  {
    q: "How is this different from the Operating Diagnostic?",
    a: "One decision versus the whole business. The Diagnostic tells you what to fix first. The Sprint tells you which way to jump on a question you already have.",
  },
  {
    q: "What do you need from me?",
    a: "The numbers you have, access to the people involved, and a deadline.",
  },
  {
    q: 'What if the answer is "do nothing"?',
    a: "Then that is the recommendation, with the trigger that would reopen it.",
  },
  {
    q: "Can it run remotely?",
    a: "Yes for the Review and the Sprint. The Deep Decision Case usually includes a site visit.",
  },
  {
    q: "What happens after?",
    a: "The memo is yours. The Decision Sprint is priced on its own and does not credit into a later service.",
  },
  ...pageFaqAdditions["/decision-sprint"],
];

export const Route = createFileRoute("/decision-sprint")({
  head: () =>
    DECISION_SPRINT_LIVE
      ? {
          meta: [
      { name: "twitter:card", content: "summary_large_image" },
            { title: TITLE },
            { name: "description", content: DESCRIPTION },
            { property: "og:title", content: TITLE },
            { property: "og:description", content: DESCRIPTION },
            { property: "og:type", content: "website" },
            { name: "twitter:title", content: TITLE },
            { name: "twitter:description", content: DESCRIPTION },
            ...ogImage("packages", "Ajility Decision Sprint"),
            ogUrl("/decision-sprint"),
          ],
          links: canonicalLinks("/decision-sprint"),
          scripts: [
            jsonLd(webPageSchema({ image: "packages", mainEntityId: `${absoluteUrl("/decision-sprint")}#service`, path: "/decision-sprint", name: TITLE, description: DESCRIPTION })),
            jsonLd(breadcrumbSchema([{ name: "Decision Sprint", path: "/decision-sprint" }])),
            jsonLd(
              serviceSchema({
                name: "Decision Sprint",
                description: DESCRIPTION,
                path: "/decision-sprint",
                slug: "decision-sprint",
                serviceType: "Decision support",
                price: findRow("Rapid Decision Review").lower ?? undefined,
              }),
            ),
            jsonLd(faqPageSchema("/decision-sprint", faqs, `${absoluteUrl("/decision-sprint")}#service`)),
          ],
        }
      : { meta: [{ title: "Packages | Ajility" }], scripts: [] },
  component: DECISION_SPRINT_LIVE ? DecisionSprintPage : DecisionSprintRedirect,
});

function DecisionSprintRedirect() {
  return <Navigate to="/packages" replace />;
}

function ScoringMatrix() {
  const reduced = useReducedMotion() ?? false;
  const rowH = 46;
  const top = 70;

  return (
    <DiagramFrame caption="Weighted criteria agreed at kick-off, applied to every option on the table.">
      <div className="sm:hidden"><div className="grid grid-cols-[minmax(0,1fr)_auto] gap-4 border-b border-border px-3 py-2 text-sm font-semibold"><span>Criterion</span><span>Weight</span></div>{criteria.map((c, i) => <div key={c.name} className={`grid grid-cols-[minmax(0,1fr)_auto] gap-4 px-3 py-3 text-sm ${i % 2 === 0 ? "bg-mist" : "bg-card"}`}><span className="min-w-0">{c.name}</span><span className="font-semibold text-midnight">{c.weight}</span></div>)}<p className="mt-3 text-xs text-charcoal">Total weight 100. Scores are shown with their assumptions.</p></div>
      <svg
        viewBox="0 0 960 400"
        role="img"
        aria-labelledby="score-title score-desc"
        className="hidden h-auto w-full max-w-full sm:block"
      >
        <title id="score-title">How options are scored</title>
        <desc id="score-desc">
          Six weighted criteria used to score every option: economic return 25, cash and
          payback 20, execution feasibility 20, operational capacity 15, downside risk 10,
          strategic fit and reversibility 10.
        </desc>

        <text x="40" y="40" fontSize="15" fontWeight="600" className="fill-foreground">
          Criterion
        </text>
        <text x="440" y="40" fontSize="15" fontWeight="600" className="fill-foreground">
          Weight
        </text>
        <text x="560" y="40" fontSize="15" fontWeight="600" className="fill-foreground">
          Applied to every option
        </text>

        {criteria.map((c, i) => {
          const y = top + i * rowH;
          return (
            <m.g key={c.name} {...rise(reduced, i * 0.06)}>
              <rect
                x="32"
                y={y - 24}
                width="896"
                height="38"
                rx="10"
                className={i % 2 === 0 ? "fill-mist" : "fill-card"}
              />
              <text x="48" y={y} fontSize="15" className="fill-foreground">
                {c.name}
              </text>
              <text x="448" y={y} fontSize="15" fontWeight="600" className="fill-midnight">
                {c.weight}
              </text>
              <rect
                x="560"
                y={y - 14}
                width={c.weight * 13}
                height="18"
                rx="9"
                className="fill-midnight"
              />
            </m.g>
          );
        })}

        <text x="48" y={top + criteria.length * rowH + 24} fontSize="14" className="fill-charcoal">
          Total weight 100. Scores are shown with their assumptions.
        </text>
      </svg>
    </DiagramFrame>
  );
}

function DecisionSprintPage() {
  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-midnight-deep pt-40 pb-24 text-frost">
        <div className="absolute inset-0 grid-lines opacity-20" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid h-12 w-12 place-items-center rounded-xl bg-white/10">
            <Compass className="h-6 w-6" aria-hidden="true" />
          </div>
          <span className="eyebrow on-dark mt-6 block">
            Decision support for founder-led businesses
          </span>
          <h1 className="mt-4 max-w-4xl font-serif text-4xl leading-tight sm:text-5xl text-balance">
            Make the big decision without spending three months circling it.
          </h1>
          <p className="mt-6 max-w-3xl text-lg leading-relaxed text-steel-on-dark">
            For businesses doing $3M to $30M, the Decision Sprint turns one high-stakes
            operational or commercial question into a clear recommendation, scored options
            and an agreed next move, in three days to three weeks. Fixed scope, fixed fee.
          </p>
          <BookFitCall
            placement="decision_sprint"
            className="mt-10"
            buttonClassName="inline-flex items-center gap-2 rounded-full bg-frost px-6 py-3 text-sm font-medium text-midnight-deep transition hover:bg-white"
            noteClassName="mt-2 text-sm font-medium text-steel-on-dark hover:text-frost"
          >
            Book a fit call <Calendar className="h-4 w-4" aria-hidden="true" />
          </BookFitCall>
        </div>
      </section>

      <PhotoBand
        src={decisionPhoto}
        alt="A leadership team working through options on a whiteboard"
        surface="background"
      />

      {/* QUESTIONS */}
      <section className="bg-background py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <span className="eyebrow block">The questions it answers</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl text-balance">
            One question, answered properly
          </h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {questions.map((q) => (
              <div key={q} className="card-panel">
                <p className="text-base leading-relaxed text-foreground">{q}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-mist py-20 sm:py-24">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <span className="eyebrow block">How it works</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl text-balance">
            Five steps, start to decision
          </h2>
          <ol className="mt-10 space-y-5">
            {steps.map((s, i) => (
              <li key={s.title} className="card-panel flex gap-5">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-midnight text-sm font-semibold text-frost">
                  {i + 1}
                </span>
                <div>
                  <h3 className="font-serif text-lg text-midnight-deep">{s.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-foreground/80">{s.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* THREE WAYS TO BUY */}
      <section className="bg-background py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <span className="eyebrow block">Three ways to buy it</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl text-balance">
            Scaled to the size of the decision
          </h2>
          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            {tiers.map((t) => (
              <TierCard key={t.name} tier={t} />
            ))}
          </div>
          <p className="mt-8 max-w-3xl text-sm text-foreground/80">{`The Decision Sprint is priced on its own and does not credit into a later service.`}</p>
        </div>
      </section>

      {/* SCORING */}
      <section className="bg-mist py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <span className="eyebrow block">How options are scored</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl text-balance">
            A weighted matrix, agreed before the work starts
          </h2>
          <div className="mt-10">
            <ScoringMatrix />
          </div>
          <p className="mx-auto mt-8 max-w-3xl text-center text-sm leading-relaxed text-foreground/80">
            The weights are agreed with you at kick-off, the scores are shown with their
            assumptions, and the memo names the two or three scores most likely to change.
            Scoring makes assumptions visible; it does not make the decision for you.
          </p>
        </div>
      </section>

      {/* IS IT FOR YOU */}
      <section className="bg-background py-20 sm:py-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <span className="eyebrow block">Is it for you</span>
          <p className="mt-4 text-lg leading-relaxed text-foreground">
            The decision should plausibly move at least $90,000 of annual profit, revenue,
            capacity or avoidable risk. If it is smaller than that, the free{" "}
            <Link
              to="/scan"
              className="font-medium text-midnight underline decoration-border underline-offset-4 transition hover:decoration-midnight"
            >
              Operating Scan
            </Link>{" "}
            and a call is the better first step.
          </p>
          <p className="mt-6 text-sm leading-relaxed text-charcoal">
            The decision, and its consequences, remain yours. Ajility provides analysis and a
            recommendation, not a guarantee of outcome, and does not give advice on financial
            products, capital raising, tax or law; where a decision turns on those, we say so
            and refer you to the right adviser.
          </p>
          <p className="mt-6 text-sm leading-relaxed text-charcoal">{SCOPE_EXCLUSION}</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-mist py-20 sm:py-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <span className="eyebrow block text-steel">FAQ</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl">
            Common questions
          </h2>
          <div className="mt-10 space-y-4">
            {faqs.map((f) => (
              <details key={f.q} className="group card-panel transition hover:border-steel/50">
                <summary className="flex min-h-11 cursor-pointer list-none flex-col justify-center -my-2 py-2 [&::-webkit-details-marker]:hidden">
                  <h3 className="flex items-center justify-between font-serif text-lg">
                    <span>{f.q}</span>
                    <ChevronRight className="h-5 w-5 shrink-0 text-steel transition group-open:rotate-90" />
                  </h3>
                </summary>
                <p className="mt-4 text-foreground/70">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <RelatedExpertise slug="strategy-execution" />
      <RelatedServices currentSlug="strategy-execution" />

      {/* CTA */}
      <section className="bg-midnight-deep py-20 text-frost sm:py-24">
        <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
          <h2 className="font-serif text-3xl sm:text-4xl">Have the decision made in weeks.</h2>
          <p className="mx-auto mt-4 max-w-2xl text-steel-on-dark">
            Bring the question. We will tell you on the call which of the three fits, and what
            it will take.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <BookFitCall
              placement="decision_sprint_footer"
              buttonClassName="inline-flex items-center gap-2 rounded-full bg-frost px-6 py-3 text-sm font-medium text-midnight-deep transition hover:bg-white"
              noteClassName="mt-2 text-sm font-medium text-steel-on-dark hover:text-frost"
            >
              Book a fit call <Calendar className="h-4 w-4" aria-hidden="true" />
            </BookFitCall>
            <Link
              to="/packages"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-medium text-frost transition hover:bg-white/10"
            >
              See the full ladder <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

function TierCard({ tier }: { tier: (typeof tiers)[number] }) {
  const viewRef = useTrackView<HTMLDivElement>(
    "decision_sprint_view",
    { tier: tier.name },
    tier.name,
  );
  return (
    <div
      ref={viewRef}
      className={`card-panel flex flex-col ${tier.standard ? "border-midnight" : ""}`}
    >
      {tier.standard ? (
        <span className="mb-3 inline-flex w-fit rounded-full bg-midnight px-3 py-1 text-xs font-medium text-frost">
          The standard choice
        </span>
      ) : null}
      <h3 className="font-serif text-xl text-midnight-deep">{tier.name}</h3>
      <p className="mt-1 text-sm font-medium text-midnight-deep">{tier.price}</p>
      <p className="mt-1 text-sm text-charcoal">{tier.duration}</p>
      <p className="mt-4 text-sm leading-relaxed text-foreground/80">{tier.forWho}</p>
      <ul className="mt-4 flex-1 space-y-2 text-sm text-foreground/80">
        {tier.youGet.map((g) => (
          <li key={g}>{g}</li>
        ))}
      </ul>
    </div>
  );
}
